<template>
		<h1 class="newsletters__heading page__heading">{{ newsHeading }}</h1>
		<div class="newsletters container">
			<nuxt-link class="newsletter"
				 v-for="newsletter in news"
				 :key="newsletter.title.en"
				 :to="localePath(`/news/${newsletter.slug.current}`)">
				<div v-if="newsletter.thumb != null"
						 class="newsletter__image-container">
					<img :src="$urlFor(newsletter.thumb.asset.url, 800)"
									 :alt="locale === 'en' ? newsletter.thumbAlt.en : newsletter.thumbAlt.jp"
									 class="newsletter__image"
									 loading="lazy"
									 :style="`object-position:${newsletter.thumbAlignment || 'middle'}`"
									 :height="newsletter.thumb.asset.metadata.dimensions.height"
									 :width="newsletter.thumb.asset.metadata.dimensions.width" />
				</div>
				<div class="newsletter__details">
					<h3 class="newsletter__title">{{ locale === 'en' ? newsletter.title.en : newsletter.title.jp }}</h3>
					<h5 class="newsletter__date" >{{formatDate(newsletter.publishedAt, locale) }}</h5>
				</div>
			</nuxt-link>
		</div>
</template>

<script setup>
import { useI18n, useLocalePath } from '#imports'
import { computed } from "vue"
import { groq } from "@nuxtjs/sanity/runtime/groq"
import { useHead } from "nuxt/app"
const { locale } = useI18n()
const localePath = useLocalePath()
const query = groq`{
	'newsIndex': *[_type == 'newsIndex'] {
		title,
		metaTitle,
    metaDescription,
	},
  'news': *[_type == 'newsLetter'] | order(publishedAt desc) {
    title,
    slug,
    thumb{
      asset->{
        metadata{
          dimensions{
            height,
            width
          }
        },
        url
      }
    },
    thumbAlt,
    thumbAlignment,
    publishedAt
  }[],
}`;
const sanity = useSanity()
const { data } = await useAsyncData('page:news-index', () => sanity.fetch(query))
const news = computed(() => data.value?.news ?? [])
const newsIndex = computed(() => data.value?.newsIndex?.[0] ?? {})
const { $getSafeLocalizedField } = useNuxtApp();
const newsHeading = computed(() => {
	if (newsIndex.value.title) {
		return locale.value === 'en' ? newsIndex.value.title.en : newsIndex.value.title.jp
	}
	return locale.value === 'en' ? 'News' : 'ニュース'
})
function formatDate(date, lang) {
  date.split('T');
  const options = { year: 'numeric', month: 'long', day: 'numeric' }
  return new Date(date).toLocaleDateString(lang, options)
}
const htmlLang = computed(() => locale.value === 'en' ? 'en-US' : 'ja-JA')
const processedTitle = computed(() => {
	if (newsIndex.value.metaTitle) {
		return locale.value === 'en' ? newsIndex.value.metaTitle.en : newsIndex.value.metaTitle.jp
	}
	return `TokyoDex - ${newsHeading.value}`
})
const fallbackDescription = 'Since 2012, TokyoDex has been a leader in transforming spaces through innovative art solutions.'
const processedDescription = computed(() => {
	const rawDescription = newsIndex.value?.metaDescription
	return $getSafeLocalizedField(rawDescription, locale.value, fallbackDescription);
});
useHead({
	htmlAttrs: {
		lang: htmlLang
	},
	title: processedTitle,
	viewport: 'width=device-width, initial-scale=1, maximum-scale=1',
	charset: 'utf-8',
	meta: [
		{ name: 'description', content: processedDescription }
	],
})
</script>

<style lang="scss" scoped>
@use '@/assets/sass/variables.scss' as v;
  .newsletters {
    display: grid;
    grid-gap: 3rem;
    grid-template-columns: 100%;
    @include v.respond-to('large') {
      grid-template-columns: repeat(2, 1fr);
    }
    &__heading {
      display: block;
      font-size: v.$font-size-xxl;
      position: relative;
      margin: 7rem 0 2rem 1rem;
			@include v.respond-to('small') {
				margin: 9.5rem 0 3rem 3rem;
			}
    }
    .newsletter {
      color: v.$mainColour;
      display: flex;
      flex-direction: column;
      height: auto;
      text-decoration: none;
      transition: 0.3s;
			@include v.respond-to('small') {
				flex-direction: row;
				height: 16rem;
			}
      &:hover {
        background-color: v.$black;
        color: v.$white;
        transition: 0.3s;
				.newsletter__details {
					.newsletter__date {
						color: v.$white;
						transition: 0.3s;
					}
				}
      }
      .newsletter__image-container {
        width: 100%;
        @include v.respond-to('small') {
          width: 50%;
					min-width: 50%;
        }
      }
      .newsletter__image {
        height: 100%;
        margin: 0;
        object-fit: cover;
        width: 100%;
        @include v.respond-to('small') {
          margin: 0 3rem 0 0;
        }
      }
      .newsletter__details {
        margin: 0;
				padding: 0 1rem;
				@include v.respond-to('small') {
					margin: 0 0 0 3rem;
					max-width: 50%;

				}
				.newsletter__title {
          font-size: v.$font-size-med;
          line-height: v.$font-size-med * 1.25;
					font-family: var(--font);
          margin: 0.5rem 0;
          max-width: 100%;
          @include v.respond-to('small') {
            margin: 1rem 3rem 1rem 0;
          }
        }
        .newsletter__date {
					color: v.$blueGrey;
					font-family: var(--font);
          font-size: v.$font-size;
          line-height: v.$font-size;
          margin: 0;
					padding: 0 0 1rem 0;
          text-transform: uppercase;
          width: 100%;
        }
      }
    }
  }
</style>
